import React, { createContext, useContext, useMemo, useState } from 'react'
import type { KibSuperSelectOption } from '@chewy/kib-fields-new-react'
import type { Nil } from '@pbt/pbt-ui-components'

import type { IdentifiedAttachmentOrigin } from '~/types'

interface ChatContainerContextType {
  pendingSelection: KibSuperSelectOption | Nil
  selectedAttachment: IdentifiedAttachmentOrigin | Nil
  setPendingSelection: (selection: KibSuperSelectOption | null) => void
  setSelectedAttachment: (attachment: IdentifiedAttachmentOrigin | null) => void
}

const ChatContainerContext = createContext<
  ChatContainerContextType | undefined
>(undefined)

export const ChatContainerProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [pendingSelection, setPendingSelection] =
    useState<KibSuperSelectOption | null>(null)

  const [selectedAttachment, setSelectedAttachment] =
    useState<IdentifiedAttachmentOrigin | null>(null)

  const contextValue = useMemo(
    () => ({
      pendingSelection,
      selectedAttachment,
      setPendingSelection,
      setSelectedAttachment,
    }),
    [
      pendingSelection,
      selectedAttachment,
      setPendingSelection,
      setSelectedAttachment,
    ],
  )

  return (
    <ChatContainerContext.Provider value={contextValue}>
      {children}
    </ChatContainerContext.Provider>
  )
}

export const useChatContainer = (): ChatContainerContextType => {
  const context = useContext(ChatContainerContext)
  if (context === undefined) {
    throw new Error(
      'useChatContainer must be used within a ChatContainerProvider',
    )
  }
  return context
}
